import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminProducts, getDashboardStats, getTopSellingProducts } from '../../services/api';
import { formatCurrency, formatCurrencyShort } from '../../utils/currency';
import { useSidebar } from '../../layouts/AdminLayout';

function Dashboard() {
  const navigate = useNavigate();
  const { sidebarOpen } = useSidebar();
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalUsers: 0,
    totalRevenue: 0,
    lowStockProducts: 0,
    pendingOrders: 0,
    todayRevenue: 0,
    growthRate: 0,
  });
  const [loading, setLoading] = useState(true);
  const [topSellingProducts, setTopSellingProducts] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          console.error('No authentication token found');
          setLoading(false);
          return;
        }

        // Fetch dashboard stats and products in parallel
        const [statsData, topSellingData, productsResponse] = await Promise.all([
          getDashboardStats(token).catch(() => null),
          getTopSellingProducts(token).catch(() => null),
          getAdminProducts(token).catch(() => [])
        ]);
        
        // Extract products array from response (handle both array and paginated object)
        const products = Array.isArray(productsResponse) 
          ? productsResponse 
          : (productsResponse?.data && Array.isArray(productsResponse.data) 
              ? productsResponse.data 
              : []);

        // Update stats from API or fallback to calculated values
        if (statsData) {
          setStats({
            totalProducts: statsData.totalProducts || 0,
            totalOrders: statsData.totalOrders || 0,
            totalUsers: statsData.totalUsers || 0,
            totalRevenue: statsData.totalRevenue || 0,
            lowStockProducts: statsData.lowStockProducts || 0,
            pendingOrders: statsData.pendingOrders || 0,
            todayRevenue: statsData.todayRevenue || 0,
            growthRate: statsData.growthRate || 0,
          });
        } else {
          // Fallback: calculate from products if stats API fails
          const totalProducts = products.length || 0;
          setStats({
            totalProducts,
            totalOrders: 0,
            totalUsers: 0,
            totalRevenue: 0,
            lowStockProducts: products.filter(p => (p.stock || 0) <= 10 && (p.stock || 0) > 0).length,
            pendingOrders: 0,
            todayRevenue: 0,
            growthRate: 0,
          });
        }

        // Update top selling products from API or fallback
        if (topSellingData && topSellingData.length > 0) {
          setTopSellingProducts(topSellingData);
        } else {
          // Fallback: show empty array if API fails
          setTopSellingProducts([]);
        }
        
        // Get low stock items from products
        const lowStock = products.filter(p => (p.stock || 0) <= 10 && (p.stock || 0) > 0);
        const outOfStock = products.filter(p => (p.stock || 0) === 0);
        setLowStockItems([...lowStock, ...outOfStock].slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        // Set defaults on error
        setStats({
          totalProducts: 0,
          totalOrders: 0,
          totalUsers: 0,
          totalRevenue: 0,
          lowStockProducts: 0,
          pendingOrders: 0,
          todayRevenue: 0,
          growthRate: 0,
        });
        setTopSellingProducts([]);
        setLowStockItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="p-6 sm:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    { 
      title: 'Products', 
      value: stats.totalProducts,
      change: '+12',
      changeType: 'positive',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      color: 'text-blue-600',
      iconBg: 'from-blue-500 to-blue-600',
      cardBg: 'from-blue-50/80 via-white to-white',
      cardBorder: 'border-blue-300/40',
      shadowColor: 'shadow-blue-200/60',
      hoverShadow: 'hover:shadow-2xl hover:shadow-blue-300/50'
    },
    { 
      title: 'Total Orders', 
      value: stats.totalOrders,
      change: '+8%',
      changeType: 'positive',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      ),
      color: 'text-emerald-600',
      iconBg: 'from-emerald-500 to-emerald-600',
      cardBg: 'from-emerald-50/80 via-white to-white',
      cardBorder: 'border-emerald-300/40',
      shadowColor: 'shadow-emerald-200/60',
      hoverShadow: 'hover:shadow-2xl hover:shadow-emerald-300/50'
    },
    { 
      title: 'Total Users', 
      value: stats.totalUsers,
      change: '+23',
      changeType: 'positive',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      color: 'text-purple-600',
      iconBg: 'from-purple-500 to-purple-600',
      cardBg: 'from-purple-50/80 via-white to-white',
      cardBorder: 'border-purple-300/40',
      shadowColor: 'shadow-purple-200/60',
      hoverShadow: 'hover:shadow-2xl hover:shadow-purple-300/50'
    },
    { 
      title: 'Revenue', 
      value: formatCurrencyShort(stats.totalRevenue),
      change: `+${stats.growthRate}%`,
      changeType: 'positive',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'text-amber-600',
      iconBg: 'from-amber-500 to-amber-600',
      cardBg: 'from-amber-50/80 via-white to-white',
      cardBorder: 'border-amber-300/40',
      shadowColor: 'shadow-amber-200/60',
      hoverShadow: 'hover:shadow-2xl hover:shadow-amber-300/50'
    },
  ];

  return (
    <div className="relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-primary/5 via-transparent to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-secondary/5 via-transparent to-transparent rounded-full blur-3xl"></div>
      </div>

      {/* Header Section */}
      <div className="mb-6 sm:mb-8 relative">
        <div className="flex items-center gap-2 sm:gap-3 mb-3">
          <div className="w-1 sm:w-1.5 h-8 sm:h-10 bg-gradient-to-b from-primary via-primary to-primary-dark rounded-full shadow-lg shadow-primary/30"></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight drop-shadow-sm">Dashboard</h1>
            <p className="text-xs sm:text-sm text-gray-600 font-medium mt-0.5 sm:mt-1">Overview of your store performance</p>
          </div>
        </div>
      </div>
      
      {/* Stats Cards - Premium Design - Horizontal Layout */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${sidebarOpen ? 'lg:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4' : 'lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4'} gap-3 sm:gap-4 md:gap-4 lg:gap-3 xl:gap-4 2xl:gap-5 mb-4 sm:mb-6 md:mb-8 transition-all duration-300`}>
        {statCards.map((stat, index) => (
          <div 
            key={index} 
            className={`relative bg-gradient-to-br ${stat.cardBg} rounded-xl sm:rounded-2xl border-2 ${stat.cardBorder} p-3 sm:p-4 lg:p-3 xl:p-4 2xl:p-5 shadow-xl ${stat.shadowColor} ${stat.hoverShadow} hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-500 group overflow-hidden`}
          >
            {/* Animated gradient overlay on hover */}
            <div className={`absolute inset-0 bg-gradient-to-br ${stat.iconBg} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
            
            {/* Decorative corner gradient */}
            <div className={`absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br ${stat.iconBg} rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500`}></div>
            
            {/* Top accent line */}
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${stat.iconBg} opacity-60`}></div>
            
            <div className="relative z-10 flex items-center gap-2 sm:gap-2.5 lg:gap-3 xl:gap-4">
              {/* Icon */}
              <div className={`relative w-9 h-9 sm:w-10 sm:h-10 lg:w-11 lg:h-11 xl:w-12 xl:h-12 bg-gradient-to-br ${stat.iconBg} rounded-lg lg:rounded-xl flex items-center justify-center text-white shadow-lg ${stat.shadowColor} flex-shrink-0 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-2xl transition-all duration-500`}>
                <div className="w-4 h-4 sm:w-4 sm:h-4 lg:w-5 lg:h-5 xl:w-5 xl:h-5">{stat.icon}</div>
                {/* Icon glow effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.iconBg} rounded-lg lg:rounded-xl opacity-50 blur-xl -z-10 group-hover:opacity-75 transition-opacity duration-500`}></div>
              </div>
              
              {/* Text Content */}
              <div className="flex-1 min-w-0">
                {/* Badge and Title Row */}
                <div className="flex items-center justify-between mb-1 sm:mb-1 lg:mb-1.5 xl:mb-2 gap-1 sm:gap-1.5 lg:gap-2">
                  <p className="text-[9px] xs:text-[10px] lg:text-xs xl:text-xs font-bold text-gray-500 uppercase tracking-wider opacity-80 truncate">{stat.title}</p>
                  <span className={`flex items-center gap-0.5 text-[8px] xs:text-[9px] lg:text-[10px] xl:text-xs font-extrabold px-1 sm:px-1.5 lg:px-2 xl:px-2.5 py-0.5 rounded-full shadow-md flex-shrink-0 ${
                    stat.changeType === 'positive' 
                      ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-2 border-emerald-400/30' 
                      : 'bg-gradient-to-r from-red-500 to-red-600 text-white border-2 border-red-400/30'
                  }`}>
                    <svg className="w-2 h-2 sm:w-2 sm:h-2 lg:w-2.5 lg:h-2.5 xl:w-3 xl:h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V13a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="whitespace-nowrap">{stat.change}</span>
                  </span>
                </div>
                
                {/* Value and Underline */}
                <div>
                  <p className={`text-base sm:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-black ${stat.color} leading-none mb-0.5 sm:mb-1 lg:mb-1.5 xl:mb-2 group-hover:scale-105 transition-transform duration-300 break-words`}>
                    {stat.value}
                  </p>
                  {/* Subtle underline accent */}
                  <div className={`w-5 sm:w-6 lg:w-8 xl:w-10 2xl:w-12 h-0.5 sm:h-0.5 lg:h-1 bg-gradient-to-r ${stat.iconBg} rounded-full opacity-60 group-hover:w-full transition-all duration-500`}></div>
                </div>
              </div>
            </div>
            
            {/* Bottom shine effect on hover */}
            <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${stat.iconBg} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
          </div>
        ))}
      </div>

      {/* Additional Stats Row - Premium Design */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${sidebarOpen ? 'lg:grid-cols-2 xl:grid-cols-3' : 'lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3'} gap-3 sm:gap-4 md:gap-4 lg:gap-3 xl:gap-4 2xl:gap-5 mb-4 sm:mb-6 md:mb-8 transition-all duration-300`}>
        {/* Low Stock Alert */}
        <div className="relative bg-gradient-to-br from-orange-50/80 via-white to-white border-2 border-orange-300/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-4 xl:p-5 2xl:p-6 shadow-xl shadow-orange-200/50 hover:shadow-2xl hover:shadow-orange-300/50 hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-500 group overflow-hidden">
          {/* Top accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-red-500 opacity-70"></div>
          {/* Decorative corner gradient */}
          <div className="absolute -top-8 -right-8 w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-orange-400 to-red-500 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-xs sm:text-sm font-bold text-gray-900 uppercase tracking-wider">Low Stock Alert</h3>
              <div className="relative w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-lg shadow-orange-300/50 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-2xl transition-all duration-500">
                <svg className="w-5 h-5 sm:w-7 sm:h-7" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl sm:rounded-2xl opacity-50 blur-xl -z-10 group-hover:opacity-75 transition-opacity duration-500"></div>
              </div>
            </div>
            <p className="text-3xl sm:text-4xl font-black text-orange-700 mb-1.5 sm:mb-2 leading-none group-hover:scale-105 transition-transform duration-300">{stats.lowStockProducts}</p>
            <p className="text-[10px] xs:text-xs font-semibold text-gray-600 uppercase tracking-wide">Products need restocking</p>
            <div className="w-8 sm:w-12 h-0.5 sm:h-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-full opacity-60 mt-2 sm:mt-3 group-hover:w-full transition-all duration-500"></div>
          </div>
        </div>

        {/* Pending Orders */}
        <div className="relative bg-gradient-to-br from-blue-50/80 via-white to-white border-2 border-blue-300/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-4 xl:p-5 2xl:p-6 shadow-xl shadow-blue-200/50 hover:shadow-2xl hover:shadow-blue-300/50 hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-500 group overflow-hidden">
          {/* Top accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 opacity-70"></div>
          {/* Decorative corner gradient */}
          <div className="absolute -top-8 -right-8 w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-xs sm:text-sm font-bold text-gray-900 uppercase tracking-wider">Pending Orders</h3>
              <div className="relative w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-300/50 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-2xl transition-all duration-500">
                <svg className="w-5 h-5 sm:w-7 sm:h-7" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl opacity-50 blur-xl -z-10 group-hover:opacity-75 transition-opacity duration-500"></div>
              </div>
            </div>
            <p className="text-3xl sm:text-4xl font-black text-blue-700 mb-1.5 sm:mb-2 leading-none group-hover:scale-105 transition-transform duration-300">{stats.pendingOrders}</p>
            <p className="text-[10px] xs:text-xs font-semibold text-gray-600 uppercase tracking-wide">Orders awaiting processing</p>
            <div className="w-8 sm:w-12 h-0.5 sm:h-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full opacity-60 mt-2 sm:mt-3 group-hover:w-full transition-all duration-500"></div>
          </div>
        </div>

        {/* Today's Revenue */}
        <div className={`relative bg-gradient-to-br from-emerald-50/80 via-white to-white border-2 border-emerald-300/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-4 xl:p-5 2xl:p-6 shadow-xl shadow-emerald-200/50 hover:shadow-2xl hover:shadow-emerald-300/50 hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-500 group overflow-hidden ${sidebarOpen ? 'sm:col-span-2 lg:col-span-1' : 'sm:col-span-2 lg:col-span-1'}`}>
          {/* Top accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 opacity-70"></div>
          {/* Decorative corner gradient */}
          <div className="absolute -top-8 -right-8 w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-xs sm:text-sm font-bold text-gray-900 uppercase tracking-wider">Today's Revenue</h3>
              <div className="relative w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-lg shadow-emerald-300/50 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-2xl transition-all duration-500">
                <svg className="w-5 h-5 sm:w-7 sm:h-7" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl sm:rounded-2xl opacity-50 blur-xl -z-10 group-hover:opacity-75 transition-opacity duration-500"></div>
              </div>
            </div>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-black text-emerald-700 mb-1.5 sm:mb-2 leading-none group-hover:scale-105 transition-transform duration-300 break-words">{formatCurrency(stats.todayRevenue)}</p>
            <p className="text-[10px] xs:text-xs font-semibold text-gray-600 uppercase tracking-wide">Revenue generated today</p>
            <div className="w-8 sm:w-12 h-0.5 sm:h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full opacity-60 mt-2 sm:mt-3 group-hover:w-full transition-all duration-500"></div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className={`grid grid-cols-1 ${sidebarOpen ? 'lg:grid-cols-2 xl:grid-cols-2' : 'lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3'} gap-3 sm:gap-4 md:gap-4 lg:gap-3 xl:gap-4 2xl:gap-5 transition-all duration-300`}>
        {/* Top Selling Products */}
        <div className={`${sidebarOpen ? 'lg:col-span-1 xl:col-span-1' : 'lg:col-span-1 xl:col-span-2 2xl:col-span-2'} relative bg-gradient-to-br from-white via-gray-50/30 to-white rounded-xl sm:rounded-2xl border-2 border-gray-300/50 shadow-xl shadow-gray-300/50 hover:shadow-2xl hover:shadow-gray-400/60 hover:-translate-y-1 transition-all duration-500 overflow-hidden group`}>
          {/* Top accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary-dark to-secondary opacity-60"></div>
          {/* Decorative corner */}
          <div className="absolute -top-12 -right-12 w-32 h-32 sm:w-40 sm:h-40 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full blur-3xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
          
          <div className="bg-gradient-to-r from-gray-50/80 via-white to-white p-4 sm:p-6 border-b-2 border-gray-200/60 relative z-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
              <div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-1 drop-shadow-sm">Top Selling Products</h2>
                <p className="text-[10px] xs:text-xs font-semibold text-gray-600 uppercase tracking-wide">Best performing products</p>
              </div>
              <button
                onClick={() => navigate('/admin/products')}
                className="text-xs sm:text-sm font-bold text-primary hover:text-primary-dark transition-all duration-300 flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl hover:bg-primary/10 border-2 border-primary/20 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/20 hover:-translate-y-0.5 self-start sm:self-auto"
              >
                View All
                <svg className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          <div className="p-4 sm:p-6 bg-white relative z-10">
            {topSellingProducts.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {topSellingProducts.map((product, idx) => (
                  <div key={product.id} className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl hover:bg-gradient-to-r hover:from-primary/5 hover:via-secondary/5 hover:to-primary/5 transition-all duration-300 group cursor-pointer border-2 border-transparent hover:border-primary/30 hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-0.5" onClick={() => navigate('/admin/products')}>
                    <div className="relative w-12 h-12 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl overflow-hidden border-2 border-gray-300 shadow-lg flex-shrink-0 group-hover:scale-110 group-hover:shadow-xl transition-all duration-300">
                      <img
                        src={product.image || product.images?.[0]}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = `https://via.placeholder.com/64/cccccc/666666?text=${encodeURIComponent(product.name.charAt(0))}`;
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      {/* Rank badge */}
                      <div className="absolute -top-1 -left-1 w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-primary to-primary-dark text-white rounded-full flex items-center justify-center text-[10px] xs:text-xs font-black shadow-lg border-2 border-white">
                        {idx + 1}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-bold text-gray-900 truncate group-hover:text-primary transition-colors mb-1.5 sm:mb-2">{product.name}</p>
                      <div className="flex flex-col xs:flex-row items-start xs:items-center gap-2 xs:gap-4 flex-wrap">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                          <span className="text-xs sm:text-sm font-semibold text-gray-700">Qty: <span className="font-bold text-emerald-700">{product.quantitySold || 0}</span></span>
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-xs sm:text-sm font-semibold text-gray-700">Value: <span className="font-bold bg-gradient-to-r from-amber-600 to-amber-700 bg-clip-text text-transparent">{formatCurrency(product.revenue || 0)}</span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-500">No sales data yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Low Stock & Quick Actions */}
        <div className="space-y-6">
          {/* Low Stock Items */}
          <div className="relative bg-gradient-to-br from-white via-red-50/20 to-white rounded-xl sm:rounded-2xl border-2 border-red-300/50 shadow-xl shadow-red-200/50 hover:shadow-2xl hover:shadow-red-300/50 hover:-translate-y-1 transition-all duration-500 overflow-hidden group">
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-500 opacity-70"></div>
            {/* Decorative corner */}
            <div className="absolute -top-12 -right-12 w-32 h-32 sm:w-40 sm:h-40 bg-gradient-to-br from-red-400/10 to-orange-400/10 rounded-full blur-3xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
            
            <div className="bg-gradient-to-r from-red-50/60 via-orange-50/40 to-white p-4 sm:p-5 border-b-2 border-red-200/60 relative z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 drop-shadow-sm">Low Stock</h2>
                <span className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-red-500 to-orange-600 border-2 border-red-400/50 text-white rounded-full flex items-center justify-center text-[10px] xs:text-xs font-black shadow-lg shadow-red-300/50 group-hover:scale-110 transition-transform duration-300">
                  {lowStockItems.length}
                </span>
              </div>
            </div>
            <div className="p-4 sm:p-5 bg-white relative z-10">
              {lowStockItems.length > 0 ? (
                <div className="space-y-2 sm:space-y-3">
                  {lowStockItems.map((product) => (
                    <div key={product.id} className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl hover:bg-gradient-to-r hover:from-red-50/80 hover:via-orange-50/60 hover:to-red-50/80 transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-red-300/50 hover:shadow-lg hover:shadow-red-200/30 hover:-translate-y-0.5 group" onClick={() => navigate('/admin/products')}>
                      <div className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl overflow-hidden border-2 border-gray-300 shadow-md flex-shrink-0 group-hover:scale-110 group-hover:shadow-lg transition-all duration-300">
                        <img
                          src={product.image || product.images?.[0]}
                          alt={product.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = `https://via.placeholder.com/56/cccccc/666666?text=${encodeURIComponent(product.name.charAt(0))}`;
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] xs:text-xs font-bold text-gray-900 truncate mb-1.5 sm:mb-2">{product.name}</p>
                        <p className={`text-[10px] xs:text-xs font-extrabold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md sm:rounded-lg inline-block shadow-sm ${
                          (product.stock || 0) > 0 
                            ? 'bg-gradient-to-r from-orange-100 to-orange-50 text-orange-700 border-2 border-orange-300/50' 
                            : 'bg-gradient-to-r from-red-100 to-red-50 text-red-700 border-2 border-red-300/50'
                        }`}>
                          {(product.stock || 0) > 0 ? `${product.stock} left` : 'Out of stock'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-xs font-medium text-gray-600">All products well stocked</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="relative bg-gradient-to-br from-white via-primary/5 to-white rounded-xl sm:rounded-2xl border-2 border-primary/30 shadow-xl shadow-primary/20 hover:shadow-2xl hover:shadow-primary/30 hover:-translate-y-1 transition-all duration-500 overflow-hidden group">
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary-dark to-secondary opacity-70"></div>
            {/* Decorative corner */}
            <div className="absolute -top-12 -right-12 w-32 h-32 sm:w-40 sm:h-40 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full blur-3xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
            
            <div className="bg-gradient-to-r from-primary/10 via-white to-white p-4 sm:p-5 border-b-2 border-primary/20 relative z-10">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 drop-shadow-sm">Quick Actions</h2>
            </div>
            <div className="p-4 sm:p-5 bg-white relative z-10 space-y-2 sm:space-y-3">
              <button
                onClick={() => navigate('/admin/products')}
                className="w-full text-left px-4 sm:px-5 py-2.5 sm:py-3.5 bg-gradient-to-r from-primary/15 via-primary/10 to-primary/15 hover:from-primary/25 hover:via-primary/15 hover:to-primary/25 text-primary rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 flex items-center gap-2 sm:gap-3 border-2 border-primary/30 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-1 group"
              >
                <div className="relative w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-primary-dark rounded-lg sm:rounded-xl flex items-center justify-center text-white shadow-lg shadow-primary/40 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:rotate-90 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 4v16m8-8H4" />
                  </svg>
                  <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary-dark rounded-lg sm:rounded-xl opacity-50 blur-md -z-10 group-hover:opacity-75 transition-opacity duration-300"></div>
                </div>
                <span className="truncate">Add New Product</span>
              </button>
              <button
                onClick={() => navigate('/admin/orders')}
                className="w-full text-left px-4 sm:px-5 py-2.5 sm:py-3.5 bg-gradient-to-r from-emerald-50 via-emerald-50/80 to-emerald-50 hover:from-emerald-100 hover:via-emerald-100/80 hover:to-emerald-100 text-emerald-700 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 flex items-center gap-2 sm:gap-3 border-2 border-emerald-300/50 hover:border-emerald-400/70 hover:shadow-xl hover:shadow-emerald-200/40 hover:-translate-y-1 group"
              >
                <div className="relative w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg sm:rounded-xl flex items-center justify-center text-white shadow-lg shadow-emerald-300/40 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg sm:rounded-xl opacity-50 blur-md -z-10 group-hover:opacity-75 transition-opacity duration-300"></div>
                </div>
                <span className="truncate">View Orders</span>
              </button>
              <button
                onClick={() => navigate('/admin/users')}
                className="w-full text-left px-4 sm:px-5 py-2.5 sm:py-3.5 bg-gradient-to-r from-purple-50 via-purple-50/80 to-purple-50 hover:from-purple-100 hover:via-purple-100/80 hover:to-purple-100 text-purple-700 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 flex items-center gap-2 sm:gap-3 border-2 border-purple-300/50 hover:border-purple-400/70 hover:shadow-xl hover:shadow-purple-200/40 hover:-translate-y-1 group"
              >
                <div className="relative w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center text-white shadow-lg shadow-purple-300/40 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl opacity-50 blur-md -z-10 group-hover:opacity-75 transition-opacity duration-300"></div>
                </div>
                <span className="truncate">Manage Users</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;